import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * **Die natürliche Höhe eines Inhalts, laufend gemessen** — damit eine Bewegung sie
 * anfahren kann.
 *
 * `height: auto` lässt sich nicht animieren: Weder CSS noch Framer bekommen einen
 * Übergang, solange der angegebene Wert `auto` bleibt und sich nur der INHALT ändert.
 * Der Wiederkehr-Kasten tut aber genau das — „Täglich" hat eine Zeile, „Wöchentlich"
 * eine mit sieben Pillen, „X-mal pro Zeitraum" eine andere, und der Satz darunter
 * wächst auf zwei Zeilen. Also wird gemessen und auf die Zahl animiert; dasselbe
 * Vorgehen wie bei der Ziel-Karte, wo `height: 'auto'` am Ende der Bewegung sprang.
 *
 * **Null wird nie gemeldet.** Ein Tab hinter `display: none` misst nur Nullen
 * (CLAUDE.md, TabStack-Falle), und in Tests meldet der nachgebaute `ResizeObserver`
 * gar nichts. Beide Fälle bleiben bei `null` — der Aufrufer nimmt dann `auto` und
 * verzichtet auf die Bewegung, statt den Inhalt auf Höhe 0 zu klemmen.
 *
 * **Gemessen wird mit `getBoundingClientRect`, nicht mit `offsetHeight`.** Das eine kennt
 * Bruchteile, das andere rundet — und ein gerundetes Viertel sackt später zurück, sobald
 * die gesetzte Höhe wegfällt.
 *
 * **Eine Bewegung braucht einen Startwert.** Wer erst einschaltet, wenn der Inhalt schon
 * gewechselt hat, misst nur noch das Ziel — und es fährt nichts, es springt. Deshalb
 * schreibt der Haken die Ruhe-Höhe auch im ausgeschalteten Zustand still mit (in einem
 * Ref, ohne Render und ohne Beobachter) und setzt sie beim Einschalten als Startwert.
 *
 * @example
 * const [measureRef, height] = useContentHeight(animated)
 * <motion.div animate={{ height: height ?? 'auto' }}><div ref={measureRef}>…</div></motion.div>
 */
export function useContentHeight(
  enabled: boolean,
  /**
   * Ob die Ruhe-Höhe gerade mitgeschrieben wird (Vorgabe: immer). Eine lange Liste schaltet es aus, wo die Zeile gerade
   * nicht aufgehen kann (#303): Der Blick je Render ist nur billig, solange nichts dazwischen schreibt — bei ⌘A erzwang
   * er in jeder gebauten Zeile ein Layout (gemessen 10.10.2026: 25 ms). Ohne Ruhe-Höhe misst das Aufgehen sofort, wie
   * vor diesem Startwert — es fährt dann nicht, sondern steht.
   */
  watchRest = true,
): [(element: HTMLElement | null) => void, number | null] {
  const [element, setElement] = useState<HTMLElement | null>(null)
  const [height, setHeight] = useState<number | null>(null)
  /** Die zuletzt gesehene Höhe im Ruhezustand — der Startwert der ersten Bewegung. */
  const ruheRef = useRef<number | null>(null)
  /** Das Element, dessen Ruhe-Höhe in `ruheRef` steht. Ein neues Element hat eine eigene. */
  const ruheFuerRef = useRef<HTMLElement | null>(null)

  const measureRef = useCallback((next: HTMLElement | null) => setElement(next), [])

  /*
    Solange nicht gemessen wird, wird trotzdem hingeschaut — einmal je Bild dieser
    Zeile, ohne Beobachter und ohne Zustand. Ein `ResizeObserver` je zusammengeklappter
    Zeile wäre in einer langen Liste Verschwendung; ein Blick auf die Maße kostet nichts
    und löst keinen Render aus.
  */
  useLayoutEffect(() => {
    if (enabled || !element || !watchRest) return
    const ruhe = element.getBoundingClientRect().height
    if (ruhe > 0) {
      ruheRef.current = ruhe
      ruheFuerRef.current = element
    }
  })

  /*
    **Der Startwert steht noch im selben Bild.** Ein Effekt käme erst nach dem Malen —
    ein Bild lang stünde `auto`, der Inhalt wäre schon auf Zielhöhe, und die Bewegung
    hätte nichts mehr zu fahren. Zustand während des Renderns anzupassen ist genau für
    diesen Fall vorgesehen: React rendert sofort noch einmal, bevor irgendetwas malt.
  */
  if (enabled && height === null && ruheRef.current !== null && ruheFuerRef.current === element) {
    setHeight(ruheRef.current)
  }

  useEffect(() => {
    if (!enabled || !element) {
      setHeight(null)
      return
    }

    const read = () => {
      const next = element.getBoundingClientRect().height
      // Siehe Kopfkommentar: Eine Null ist keine Höhe, sondern ein verstecktes Elternteil.
      if (next > 0) setHeight(next)
    }

    /*
      Ohne bekannte Ruhe-Höhe sofort messen (so war es immer). MIT ihr NICHT: Das
      Sofort-Messen überschriebe den Startwert im selben Schwung mit der Zielhöhe,
      und die Bewegung fiele wieder aus. Die Zielhöhe liefert der Beobachter gleich
      darauf von selbst — er meldet sich nach dem Anhängen einmal ungefragt.
    */
    if (ruheRef.current === null || ruheFuerRef.current !== element) read()

    const observer = new ResizeObserver(read)
    observer.observe(element)
    return () => observer.disconnect()
  }, [enabled, element])

  return [measureRef, height]
}
